import type { DetectionCategory, LocalAiProfile, MaskSettings, MemoryProfile } from "@aimosaic/core";
import { DEFAULT_MASK_SETTINGS } from "@aimosaic/core";
import type { Appearance, Locale } from "@aimosaic/ui";

const DB_NAME = "aimosaic-preferences";
const STORE_NAME = "settings";
const DB_VERSION = 1;

export interface StoredPreferences {
  categories: DetectionCategory[];
  maskSettings: MaskSettings;
  memory: MemoryProfile;
  locale: Locale;
  appearance: Appearance;
  deepSearch: boolean;
  localAi: LocalAiSettings;
  panelState: PanelState;
}

export interface LocalAiSettings {
  enabled: boolean;
  endpoint: string;
  mode: "review-only" | "precise";
  profile: LocalAiProfile;
}

export const DEFAULT_LOCAL_AI_SETTINGS: LocalAiSettings = {
  enabled: false,
  endpoint: "/api/local-ai",
  mode: "review-only",
  profile: "fast"
};

export type PanelId = "preset" | "detectionOptions" | "batch" | "maskOptions" | "localAi" | "detections" | "export";
export type PanelState = Record<PanelId, boolean>;

export const DEFAULT_PANEL_STATE: PanelState = {
  preset: false,
  detectionOptions: false,
  batch: true,
  maskOptions: false,
  localAi: false,
  detections: true,
  export: false
};

export const DEFAULT_CATEGORIES: DetectionCategory[] = [
  "ip",
  "url",
  "email",
  "token",
  "secret",
  "resident-id",
  "phone",
  "face"
];

const SUPPORTED_PRESET_CATEGORIES = new Set<DetectionCategory>(DEFAULT_CATEGORIES);

export async function loadPreferences(): Promise<StoredPreferences> {
  return {
    categories: normalizeCategories(await getValue<DetectionCategory[]>("categories")),
    maskSettings: normalizeMaskSettings(await getValue<Partial<MaskSettings>>("maskSettings")),
    memory: (await getValue<MemoryProfile>("memory")) ?? { ignoredTexts: [] },
    locale: (await getValue<Locale>("locale")) ?? "ko",
    appearance: (await getValue<Appearance>("appearance")) ?? "system",
    deepSearch: (await getValue<boolean>("deepSearch")) ?? false,
    localAi: normalizeLocalAiSettings(await getValue<Partial<LocalAiSettings>>("localAi")),
    panelState: normalizePanelState(await getValue<Partial<PanelState>>("panelState"))
  };
}

export async function saveCategories(categories: DetectionCategory[]): Promise<void> {
  await setValue("categories", normalizeCategories(categories));
}

export async function saveMaskSettings(settings: MaskSettings): Promise<void> {
  await setValue("maskSettings", normalizeMaskSettings(settings));
}

export async function saveLocale(locale: Locale): Promise<void> {
  await setValue("locale", locale);
}

export async function saveAppearance(appearance: Appearance): Promise<void> {
  await setValue("appearance", appearance);
}

export async function saveDeepSearch(enabled: boolean): Promise<void> {
  await setValue("deepSearch", enabled);
}

export async function saveLocalAiSettings(settings: LocalAiSettings): Promise<void> {
  await setValue("localAi", normalizeLocalAiSettings(settings));
}

export async function savePanelState(settings: PanelState): Promise<void> {
  await setValue("panelState", normalizePanelState(settings));
}

export async function rememberIgnoredText(text: string): Promise<MemoryProfile> {
  const memory = (await getValue<MemoryProfile>("memory")) ?? { ignoredTexts: [] };
  const ignoredTexts = Array.from(new Set([...(memory.ignoredTexts ?? []), text]));
  const next = { ...memory, ignoredTexts };
  await setValue("memory", next);
  return next;
}

async function getValue<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

async function setValue<T>(key: string, value: T): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const request = db.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME).put(value, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function normalizeLocalAiSettings(value: Partial<LocalAiSettings> | undefined): LocalAiSettings {
  const endpoint = typeof value?.endpoint === "string" && value.endpoint.trim()
    ? value.endpoint.trim()
    : DEFAULT_LOCAL_AI_SETTINGS.endpoint;
  const profile = value?.profile === "precise" ? "precise" : "fast";
  const mode = value?.mode === "precise" ? "precise" : "review-only";
  return {
    enabled: Boolean(value?.enabled),
    endpoint,
    mode,
    profile
  };
}

function normalizeMaskSettings(value: Partial<MaskSettings> | undefined): MaskSettings {
  const mode = value?.mode === "opaque" || value?.mode === "blur" || value?.mode === "pixelate"
    ? value.mode
    : DEFAULT_MASK_SETTINGS.mode;
  return {
    mode,
    textLevel: "full",
    pixelSize: Number.isFinite(value?.pixelSize) ? Number(value?.pixelSize) : DEFAULT_MASK_SETTINGS.pixelSize,
    blurRadius: Number.isFinite(value?.blurRadius) ? Number(value?.blurRadius) : DEFAULT_MASK_SETTINGS.blurRadius,
    fillColor: typeof value?.fillColor === "string" && value.fillColor.trim()
      ? value.fillColor
      : DEFAULT_MASK_SETTINGS.fillColor
  };
}

function normalizeCategories(value: DetectionCategory[] | undefined): DetectionCategory[] {
  const categories = Array.isArray(value) ? value.filter((category) => SUPPORTED_PRESET_CATEGORIES.has(category)) : [];
  return categories.length > 0 ? Array.from(new Set(categories)) : DEFAULT_CATEGORIES;
}

function normalizePanelState(value: Partial<PanelState> | undefined): PanelState {
  return {
    preset: typeof value?.preset === "boolean" ? value.preset : DEFAULT_PANEL_STATE.preset,
    detectionOptions: typeof value?.detectionOptions === "boolean"
      ? value.detectionOptions
      : DEFAULT_PANEL_STATE.detectionOptions,
    batch: typeof value?.batch === "boolean" ? value.batch : DEFAULT_PANEL_STATE.batch,
    maskOptions: typeof value?.maskOptions === "boolean" ? value.maskOptions : DEFAULT_PANEL_STATE.maskOptions,
    localAi: typeof value?.localAi === "boolean" ? value.localAi : DEFAULT_PANEL_STATE.localAi,
    detections: typeof value?.detections === "boolean" ? value.detections : DEFAULT_PANEL_STATE.detections,
    export: typeof value?.export === "boolean" ? value.export : DEFAULT_PANEL_STATE.export
  };
}
